import {NodeType, Node, Fragment, Slice, Schema, Attrs } from "prosemirror-model";
import { splitListItem } from "prosemirror-schema-list";
import {Command, TextSelection, EditorState, Transaction} from "prosemirror-state";


export function wrapInDescList(dlType: NodeType, dtType: NodeType, ddType: NodeType): Command {
	return (state, dispatch) => {
		const { selection, tr, schema } = state;
		const { $from, $to } = selection;

		// Ensure the selection spans block content
		const range = $from.blockRange($to);
		if (!range) return false;

		// Extract the selected content as a Fragment
		const startOffset = range.start - range.$from.start(range.depth);
		const endOffset = range.end - range.$from.start(range.depth);
		const selectedFragment = range.parent.content.cut(startOffset, endOffset);

		let index = 0;
		let transformedContent = [];

		// Iterate through the top-level nodes in the fragment
		selectedFragment.forEach(node => {
			if (node.isBlock) {
				// Convert paragraphs to dt or dd based on their index
				const newNodeType = index % 2 === 0 ? dtType : ddType;
				const newNode = collapseToSingleNodeWithInline(node, newNodeType);
				transformedContent.push(newNode);
				index++;
			}
		});

		// Create a new DL node with the transformed content
		const dlNode = dlType.create(null, Fragment.from(transformedContent));

		// Create a Slice from the dlNode
		const fragment = Fragment.from(dlNode); // The content of the Slice
		//console.log("Length of the fragment is " + fragment.size + " and start "+range.start);
		const slice = new Slice(fragment, 0, 0);

		// Replace the selection with the new DL node
		tr.replaceRange(range.start, range.end, slice);

		// Position the selection at the end of newly created list
		tr.setSelection(TextSelection.create(tr.doc, range.start + fragment.size - 2));


		if (dispatch) {
			dispatch(tr);
		}

		return true;
	};
}



/**
 * Collapses a multi-level node structure into a single node while preserving marks and inline nodes.
 *
 * @param node - The ProseMirror node to flatten.
 * @param newNodeType - Type of the new node to which will be text collapsed.
 * @param attrs optional attributes of the new node
 * @returns A single node containing the collapsed content with marks and inline nodes preserved.
 */
function collapseToSingleNodeWithInline(node: Node, newNodeType:NodeType, attrs?:Attrs): Node {
	let content:Node[] = [];
	const schema = node.type.schema;

	function processChild(child: Node, isFirst: boolean = false): void {
		if (child.isText) {
			// Add text nodes with their marks
			content.push(schema.text(child.text, child.marks));
		} else if (child.isInline) {
			// Add inline nodes (e.g., image nodes with marks/attributes)
			content.push(child.copy(child.content));
		} else if (child.isBlock) {
			// Recursively process block and node children
			if (!isFirst && content.length > 0) content.push(schema.text(" "));
			child.forEach((nestedChild, offset, index) => processChild(nestedChild, index === 0));
		}
	}

	// Start processing from the root node
	node.forEach((child, offset, index) => processChild(child, index === 0));

	// Create a new node of type `node` with the collected content
	return newNodeType.create(attrs, Fragment.fromArray(content));
}


export const enterKeyInDList:Command = (state:EditorState, dispatch?) => {
	// Print cursor info
	let {$cursor} = state.selection as TextSelection
	if (!$cursor) return false;
	//console.log("Depth " + $cursor.depth + " is node " + $cursor.parent.type.name+" pos="+$cursor.pos);

	const parentType = $cursor.parent.type;
	const dtType = state.schema.nodes.dt;
	const ddType = state.schema.nodes.dd;

	if (parentType === dtType || parentType === ddType) {
		const isEmpty = $cursor.parent.content.size === 0;
		const cursorAtStart = $cursor.pos === $cursor.start();
		const cursorAtEnd = $cursor.pos === $cursor.end();
		if (isEmpty) {
			// We should lift DT/DD to paragraph
			const grandparent = $cursor.node($cursor.depth - 1);
			if (!grandparent || grandparent.type !== state.schema.nodes.dl) return false;

			const pos = $cursor.pos; // Cursor position
			const index = $cursor.index($cursor.depth - 1 /*this level points to DL*/); // Index of the dt/dd in the dl
			const dlStart = $cursor.start($cursor.depth - 1); // Start of the dl
			const dlEnd = $cursor.end($cursor.depth - 1); // End of the dl

			if (index === 0) return false;	// the first dt/dd block inside dl (not handling)

			// Determine if there are siblings before or after
			const hasAfter = dlEnd > pos + 1;

			const tr = state.tr;
			tr.delete(pos - 1, pos + 1);	// Remove the empty dt/dd
			if (hasAfter) {
				tr.split(pos - 1); // Split the <dl> into two
			}
			tr.insert(pos, state.schema.nodes.paragraph.createAndFill());

			if (dispatch) dispatch(tr.scrollIntoView());
			return true;
		} else if (cursorAtEnd) {
			// We should alter DT/DDs
			if (dispatch) {
				const tr = state.tr;
				tr.split($cursor.pos, 1 , [{ type: (parentType === dtType ? ddType : dtType)}]);
				dispatch(tr.scrollIntoView());
				return true;
			}
		}
	}
	return false;
};

export const enterKeyInListItem:Command = (state:EditorState, dispatch?) => {
	const liType = state.schema.nodes.li;
	if (!liType) return false;
	return splitListItem(liType)(state, dispatch);
}
